/**
 * 网格生成 — 按配置确定性地生成全部 Build Cell。
 * 使用基于 (layer, ring, sector) 的整数哈希做属性变化，保证确定性（无随机源）。
 */

import {
  sectorsOfRing,
  DEFAULT_MAP_CONFIG,
  cellKey,
  type BuildCell,
  type BuildCellId,
  type BuildLayer,
  type SpatialMapConfig,
  type SpatialMapData,
  LAYER_ORDER,
} from './types.js';

/** 整数确定性伪随机（仅由坐标决定，供属性微扰；不引入运行时随机源）。 */
function coordNoise(layer: BuildLayer, ring: number, sector: number): number {
  let h = 2166136261 >>> 0;
  const mix = (v: number) => {
    h ^= v & 0xff;
    h = Math.imul(h, 16777619);
  };
  for (const ch of layer) mix(ch.charCodeAt(0));
  mix(ring);
  mix(sector);
  mix(ring >>> 8);
  mix(sector >>> 8);
  h ^= h >>> 13;
  h = Math.imul(h, 0x5bd1e995);
  h ^= h >>> 15;
  return (h >>> 0) / 4294967296; // [0,1)
}

/** 生成单个 Cell 的初始属性（确定性）。
 * 属性带空间相关性（相邻 cell 的属性相近），并保证坑底存在成片可建平地。
 */
function makeCell(layer: BuildLayer, ring: number, sector: number, config: SpatialMapConfig): BuildCell {
  const n = coordNoise(layer, ring, sector);
  // 低频相关分量：相邻 sector 的属性相近（模拟连续地形）
  const nSec = sectorsOfRing(config, layer, ring);
  const nPrev = coordNoise(layer, ring, (sector + nSec - 1) % nSec);
  const nNext = coordNoise(layer, ring, (sector + 1) % nSec);
  const smooth = (n + nPrev + nNext) / 3; // [0,1)，空间平滑
  const isWall = layer === 'wall';
  const isSub = layer === 'subsurface';
  // 坡度：floor 用平滑后的低幅值，确保成片平地（2×2 易满足 maxSlope 1200）
  const slope = isWall ? Math.round(1500 + smooth * 2500) : Math.round(smooth * 700);
  const support = isWall ? Math.round(30 + smooth * 40) : Math.round(45 + smooth * 45);
  const contamination = Math.round(smooth * 35);
  return {
    layer,
    ring,
    sector,
    // 地下层外环初始不可见（需勘探揭示），其余可见
    visible: !(isSub && ring >= 5),
    buildable: !isSub || ring < 6,
    excavatable: isSub,
    slopeBasisPoints: slope,
    support,
    fracture: Math.round(smooth * 2000),
    contamination,
    temperature: isSub ? Math.round(-20 - ring * 2) : Math.round(-40 + smooth * 10),
    waterContent: isSub ? Math.round(3000 + smooth * 4000) : Math.round(smooth * 1500),
    occupiedBy: null,
    hazardLevel: 0,
    district: null,
  };
}

/** 生成整张地图（确定性）。 */
export function generateMap(config: SpatialMapConfig = DEFAULT_MAP_CONFIG): SpatialMapData {
  const cells: Record<string, BuildCell> = {};
  for (const layer of LAYER_ORDER) {
    const rings = config.ringsPerLayer[layer];
    for (let ring = 0; ring < rings; ring++) {
      const sectors = sectorsOfRing(config, layer, ring);
      for (let sector = 0; sector < sectors; sector++) {
        const cell = makeCell(layer, ring, sector, config);
        cells[cellKey(cell)] = cell;
      }
    }
  }
  return { config, cells };
}

/** 取 Cell（不存在返回 undefined）。 */
export function getCell(map: SpatialMapData, id: BuildCellId): BuildCell | undefined {
  return map.cells[cellKey(id)];
}

/** 校验 ID 是否在地图边界内。 */
export function isValidCellId(map: SpatialMapData, id: BuildCellId): boolean {
  if (!Number.isInteger(id.ring) || !Number.isInteger(id.sector)) return false;
  if (id.ring < 0 || id.ring >= map.config.ringsPerLayer[id.layer]) return false;
  if (id.sector < 0 || id.sector >= sectorsOfRing(map.config, id.layer, id.ring)) return false;
  return true;
}
